/**
 * The drawer's Notes tab body (Evo/PSC). A single chronological feed (newest
 * first) merging the current exception's thread with notes from sibling
 * exceptions on the same record. Consumes the {@link DrawerNotes} bundle from
 * ``useDrawerNotes``; the composer that posts into it is the tab's footer
 * (``NotesComposer``). No accordion chrome - the tab is the thread.
 */
import React from 'react'
import { Box, Divider, Stack, Typography } from '@mui/material'
import NoteItem from '../NoteItem'
import type { DrawerNotes } from './useDrawerNotes'

interface NotesPanelProps {
  notes: DrawerNotes
  fpCode: string
  fpId?: number
}

const NotesPanel: React.FC<NotesPanelProps> = ({ notes, fpCode, fpId }) => {
  const {
    noteFeed,
    loading,
    error,
    siblingLoading,
    siblingError,
    handleEdit,
    handleDelete,
  } = notes

  if ((loading || siblingLoading) && noteFeed.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
        Loading...
      </Typography>
    )
  }
  if (error || siblingError) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 0.5 }}>
        {error || siblingError}
      </Typography>
    )
  }
  if (noteFeed.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}
      >
        No notes yet
      </Typography>
    )
  }

  // Newest first, separated by hairline dividers (no per-note cards).
  return (
    <Box>
      <Stack
        divider={<Divider sx={{ borderColor: 'divider', opacity: 0.6 }} />}
        spacing={0}
      >
        {noteFeed.map((item) => (
          <NoteItem
            key={item.key}
            note={item.note}
            exceptionName={item.exceptionName}
            exceptionCode={item.exceptionCode}
            dataDomain={item.dataDomain}
            isCurrent={item.source === 'current'}
            fpCode={fpCode}
            fpId={fpId}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}
      </Stack>
    </Box>
  )
}

export default NotesPanel
